import { useEffect, useMemo, useState } from 'react'

// Absolute card_image values are already display-ready (owned CDN, etc.).
// Legacy relative values are served from the bundled /cards/ directory.
function getPrimaryUrl(cardImage) {
  if (!cardImage) return null
  if (/^https?:\/\//i.test(cardImage)) return cardImage
  const filename = cardImage.replace(/^.*\//, '')
  return `/cards/${filename}`
}

function initialsFor(fragrance) {
  const house = (fragrance?.house || '').trim()
  if (!house) return 'FO'
  const words = house.split(/\s+/).filter(Boolean)
  if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase()
  return house.slice(0, 2).toUpperCase()
}

const BottleFallback = ({ fragrance }) => (
  <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
    <svg
      className="absolute h-[72%] w-[72%] text-cream/[0.045]"
      viewBox="0 0 120 160"
      fill="none"
      aria-hidden="true"
    >
      <path d="M49 13h22v25c0 4 2 8 5 11l8 7c8 7 13 17 13 28v47c0 10-8 18-18 18H41c-10 0-18-8-18-18V84c0-11 5-21 13-28l8-7c3-3 5-7 5-11V13Z" stroke="currentColor" strokeWidth="7" />
      <path d="M44 13h32" stroke="currentColor" strokeWidth="7" strokeLinecap="round" />
    </svg>
    <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-gold/25 bg-charcoal-900/50">
      <span className="font-body text-2xl font-semibold tracking-normal text-gold/85">
        {initialsFor(fragrance)}
      </span>
    </div>
  </div>
)

export default function FragranceImage({ fragrance, className = '', imgClassName = '', alt, fill = false }) {
  const imageFlagged = fragrance?.image_flagged === true
  const primaryUrl = useMemo(
    () => imageFlagged ? null : getPrimaryUrl(fragrance?.card_image),
    [fragrance?.card_image, imageFlagged]
  )
  // Two-tier: card_image → fallback SVG. Fragrantica-hosted images are banned
  // (rule 8) — enforced at build time by scripts/assert-no-fragrantica-images.mjs.
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [primaryUrl])

  function handleError() {
    setFailed(true)
  }

  return (
    <div
      className={`relative ${fill ? 'h-full w-full' : 'aspect-[4/5] w-full'} overflow-hidden rounded-lg bg-white ${className}`}
    >
      {primaryUrl && !failed ? (
        <img
          src={primaryUrl}
          alt={alt ?? [fragrance?.house, fragrance?.name].filter(Boolean).join(' ') ?? ''}
          className={`h-full w-full object-contain ${imgClassName}`}
          loading="lazy"
          onError={handleError}
        />
      ) : (
        <BottleFallback fragrance={fragrance} />
      )}
      {/* Top-left: the top-right corner is reserved for card actions (overflow menu). */}
      {imageFlagged && (
        <div
          className="absolute left-2 top-2 h-2.5 w-2.5 rounded-full border border-amber-200/40 bg-amber-300/65 shadow-[0_0_0_3px_rgba(251,191,36,0.12)]"
          title="Image under review"
          aria-label="Image under review"
        />
      )}
    </div>
  )
}
